import { c as _c } from "react/compiler-runtime";
import type { CheckoutRequest } from '@/app/api/billing/checkout/types';
import type { PlanChangeRequest, PlanChangeResponse } from '@/app/api/billing/plan-change/types';
import type { ResumeSetupRequest } from '@/app/api/billing/resume/types';
import type { BillingInterval, PaidPlan } from '@/types/billing-plan';
import { useState } from 'react';
import { SETTINGS_TABS } from '@/lib/constants';
import { baseUrl } from '@/lib/env';
import { buildSettingsPath } from '@/utils/app-paths';

/**
 * Builds Stripe return URLs for checkout and resume flows.
 * We keep this centralized so both client flows stay in sync with query-param conventions.
 */
function getUrls(isResume: boolean) {
  const successPath = buildSettingsPath({
    tab: SETTINGS_TABS.BILLING,
    params: isResume ? {
      resume_success: true
    } : {
      checkout_success: true
    }
  });
  const cancelPath = buildSettingsPath({
    tab: SETTINGS_TABS.BILLING,
    params: isResume ? {
      resume_cancelled: true
    } : {
      checkout_cancelled: true
    }
  });
  const successUrl = `${baseUrl()}${successPath}`;
  const cancelUrl = `${baseUrl()}${cancelPath}`;
  return {
    successUrl,
    cancelUrl
  };
}

/**
 * Calls a billing session endpoint and returns the Stripe redirect URL.
 */
async function createBillingSession(endpoint: string, payload: CheckoutRequest | ResumeSetupRequest, errorMessage: string): Promise<string> {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || errorMessage);
  }
  return data.url;
}

/**
 * Encapsulates pricing page actions that can start Stripe-managed billing flows.
 */
export function usePricingActions() {
  const $ = _c(2);
  const [isLoading, setIsLoading] = useState(false);
  const startCheckoutFlow = _temp;
  const startResumeFlow = _temp2;
  const startPlanChangeFlow = _temp3;
  let t0;
  if ($[0] !== isLoading) {
    t0 = {
      isLoading,
      setIsLoading,
      startCheckoutFlow,
      startResumeFlow,
      startPlanChangeFlow
    };
    $[0] = isLoading;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
async function _temp3(t0) {
  const {
    installationId: installationId_1,
    targetPlan,
    quantity: quantity_1
  } = t0;
  const request_1 = {
    installationId: installationId_1,
    targetPlanTier: targetPlan,
    quantity: quantity_1
  };
  const response = await fetch("/api/billing/plan-change", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(request_1)
  });
  const data = (await response.json()) as PlanChangeResponse;
  if (!response.ok || !data.success) {
    const errorMessage = "error" in data ? data.error : "Failed to change plan";
    throw new Error(errorMessage);
  }
  return data;
}
async function _temp2(t0) {
  const {
    installationId: installationId_0,
    quantity: quantity_0,
    autoAssignSeats: autoAssignSeats_0
  } = t0;
  const {
    successUrl: successUrl_0,
    cancelUrl: cancelUrl_0
  } = getUrls(true);
  const request_0 = {
    installationId: installationId_0,
    quantity: quantity_0,
    successUrl: successUrl_0,
    cancelUrl: cancelUrl_0,
    autoAssignSeats: autoAssignSeats_0
  };
  return createBillingSession("/api/billing/resume/setup", request_0, "Failed to create setup session");
}
async function _temp(t0) {
  const {
    installationId,
    quantity,
    billingCycle,
    plan,
    autoAssignSeats
  } = t0;
  const {
    successUrl,
    cancelUrl
  } = getUrls(false);
  const request = {
    installationId,
    quantity,
    successUrl,
    cancelUrl,
    billingInterval: billingCycle,
    planTier: plan,
    autoAssignSeats
  };
  return createBillingSession("/api/billing/checkout", request, "Failed to create checkout session");
}